import React, { useState } from "react";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import { PrimaryButton } from "../../components/ui/Button";
import { InputField } from "../../components/ui/Inputs";
import { ArrowForward } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import MainHeading from "../../components/ui/MainHeading";
import PaperCard from "../../components/ui/PaperCard";
import { ReactComponent as EyeOffIcon } from "../../assets/icons/auth/eye-off.svg";
import { ReactComponent as EyeIcon } from "../../assets/icons/auth/eye.svg";
import Toast from "../../components/ui/Toast";
import userProfileServices from "../../services/api/service/userProfileService";
import { useDispatch, useSelector } from "react-redux";
import { authSelector } from "../../services/redux/slices/authSlice";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import regex from "../../data/regex";
import { trackEvent } from "../../utils/ga";
import { decrypt } from "../../utils/crypto";

interface FormInputs {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ChangePassword: React.FC = () => {
  const dispatch = useDispatch();
  const { userProfile } = useSelector(authSelector);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<
    "error" | "warning" | "info" | "success"
  >("success");
  const [showPassword, setShowPassword] = useState(false);
  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
  } = useForm<FormInputs>();

  const oldPassword = watch("oldPassword");
  const newPassword = watch("newPassword");

  const handleToastOpen = (
    message: string,
    severity: "error" | "warning" | "info" | "success"
  ) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleChangePassword = async (data: any) => {
    try {
      const res = await userProfileServices.changeUserPassword(
        userProfile?.id,
        data
      );
      if (res.status == 200) {
        dispatch(
          setStatusPopup({
            status: "success",
            message: "Profile Password Updated successfully",
            locationPath: "/profile",
          })
        );
      }
    } catch (error: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message,
          locationPath: "/profile",
        })
      );
    }
  };

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    trackEvent('Change_Password_Submit', decrypt(userProfile?.id), {
      label: 'Change_Password_Submit',
      category: 'Change_Password_Submit',
      action: "Change_Password_Submit",
    });
    
    // Logic to handle password change goes here
    handleChangePassword(data);
  };

  return (
    <Box>
      <MainHeading title="Change password" backButtonVisible={true} />
      <Grid container>
        <Grid item xs={12}>
          <PaperCard>
            <Grid container alignItems="center">
              <Grid item xs={12} sm={6}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  {/* Old Password Field */}
                  <InputField
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    label="Enter old password"
                    {...register("oldPassword", {
                      required: "Old password is required",
                      minLength: {
                        value: 8,
                        message:
                          "Old Password must be at least 8 characters long",
                      },
                      maxLength: {
                        value: 15,
                        message:
                          "Old Password must be at most 15 characters long",
                      },
                      pattern: {
                        value: regex?.password,
                        message:
                          "Old Password must contain one capital letter and symbol @$!%*?& or numbers",
                      },
                    })}
                    error={!!errors.oldPassword}
                    helperText={errors?.oldPassword?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            edge="end"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ?  <EyeIcon />:<EyeOffIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />

                  {/* New Password Field */}
                  <InputField
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    label="Enter new password"
                    {...register("newPassword", {
                      required: "New password is required",
                      minLength: {
                        value: 8,
                        message: "Password must be at least 8 characters long",
                      },
                      maxLength: {
                        value: 15,
                        message: "Password must be at most 15 characters long",
                      },
                      pattern: {
                        value: regex?.password,
                        message:
                          "Password must contain one capital letter and symbol @$!%*?& or numbers",
                      },
                      validate: (value) =>
                        value !== oldPassword ||
                        "New password can not be same as old password  ",
                    })}
                    error={!!errors.newPassword}
                    helperText={errors?.newPassword?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            edge="end"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ?  <EyeIcon />:<EyeOffIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  {/* Confirm New Password Field */}
                  <InputField
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    label="Confirm new password"
                    {...register("confirmPassword", {
                      required: "Confirm password is required",
                      validate: (value) =>
                        value === newPassword || "Passwords do not match",
                    })}
                    error={!!errors.confirmPassword}
                    helperText={errors?.confirmPassword?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            edge="end"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeIcon />:<EyeOffIcon /> }
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    onBlur={() => {
                      if (newPassword && errors.confirmPassword) {
                        errors.confirmPassword.message =
                          "Passwords do not match";
                      }
                    }}
                    sx={{ mb: 2 }}
                  />

                  {/* Validation Messages */}
                  <Box component="ul" sx={{ mt: 2, mb: 2, pl: 2 }}>
                    <Typography component="li">
                      Between 8-15 characters without spacing
                    </Typography>
                    <Typography component="li">
                      Contains a combination of uppercase letters, lowercase
                      letters, numbers, and special characters
                    </Typography>
                  </Box>

                  {/* Submit Button */}
                  <PrimaryButton
                    type="submit"
                    fullWidth
                    endIcon={<ArrowForward color="inherit" fontSize="small" />}
                  >
                    Submit
                  </PrimaryButton>
                </form>
              </Grid>
            </Grid>
          </PaperCard>
        </Grid>
      </Grid>
      <Toast
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={() => setToastOpen(false)}
      />
    </Box>
  );
};

export default ChangePassword;
